import { OverlayModule } from '@angular/cdk/overlay'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  InputSignal,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms'

import { pick as _pick } from 'lodash/fp'

import { Observable, debounceTime, of, switchMap } from 'rxjs'
import { take } from 'rxjs/operators'

import { FlxThemeModule } from '@flowx/angular-theme'
import {
  FlxButtonModule,
  FlxCardModule,
  FlxIconModule,
  FlxInputModule,
  FlxSelectModule,
} from '@flowx/angular-ui-toolkit'
import {
  ExecuteTaskActions,
  TaskActions,
  User,
  ViewTask,
  getPossibleBulkActions,
  localizeString,
} from '@flowx/core-sdk'

import { FlxLocalizePipe } from '../../../pipes/flx-localize.pipe'
import { ViewTasksStateService } from '../../services/view-tasks-state.service'
import { MAP_ASSIGNEES } from '../../task.helpers'

@Component({
  selector: 'flx-bulk-tasks',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FlxCardModule,
    FlxInputModule,
    FlxIconModule,
    FlxSelectModule,
    FlxThemeModule,
    OverlayModule,
    FlxLocalizePipe,
    FlxButtonModule,
  ],
  templateUrl: './flx-bulk-tasks.component.html',
  host: {
    '[class]': `'flx-bulk-tasks'`,
  },
  styleUrl: './flx-bulk-tasks.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class FlxBulkTasksComponent implements OnInit, OnDestroy {
  #taskService = inject(ViewTasksStateService)
  #destroyRef = inject(DestroyRef)

  selectedTasks: InputSignal<ViewTask[]> = input([])
  mapForBulkTasks = computed(() =>
    this.selectedTasks().map((task) => _pick(['id', 'tokenUuid'], task))
  )
  possibleActions = computed(() => getPossibleBulkActions(this.selectedTasks()))
  username = new FormControl('')
  bulkAssignees = signal<{ value: string; label: string }[]>([])
  loading = signal(false)
  assigneesCalled = signal(false)
  emptyMessage = computed(() => {
    const assigneesCalled = this.assigneesCalled()
    const loading = this.loading()
    const bulkAssignees = this.bulkAssignees()
    if (!assigneesCalled) {
      return localizeString('@@sys_tm_search_term_validation')
    }

    if (loading) {
      return localizeString('@@sys_tm_loading')
    }

    if (bulkAssignees.length === 0) {
      return localizeString('@@sys_tm_nothing_found')
    }

    return ''
  })
  hideBulk = output<string>()
  startBulk = output()
  isOpen = signal(false)
  assignAction = TaskActions.ASSIGN

  constructor() {
    effect(() => {
      const existingTasks = this.selectedTasks().length > 0

      if (!existingTasks) {
        this.closeOverlay()
      }
    })
  }

  ngOnInit(): void {
    this.username.valueChanges
      .pipe(
        debounceTime(300),
        switchMap((value) => this.getBulkAssignees(value)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((users) => {
        this.bulkAssignees.set(MAP_ASSIGNEES(users))
        this.loading.set(false)
      })
  }

  toggleOverlay(): void {
    this.isOpen.set(!this.isOpen())
  }

  closeOverlay(): void {
    this.isOpen.set(false)
  }

  doAction(action: ExecuteTaskActions): void {
    if (action === this.assignAction) {
      this.toggleOverlay()
    } else {
      this.bulkAction(action)
      this.closeOverlay()
    }
  }

  getBulkAssignees(keyword: string): Observable<User[]> {
    if (keyword?.length >= 4) {
      this.assigneesCalled.set(true)
      this.loading.set(true)
      return this.#taskService.fetchBulkAssignees(this.mapForBulkTasks(), { keyword }).pipe(take(1))
    } else {
      this.assigneesCalled.set(false)
      return of([])
    }
  }

  bulkAction(action: ExecuteTaskActions, username?: string): void {
    const tasks = this.selectedTasks()
    this.startBulk.emit()
    this.#taskService
      .bulkUpdate(this.mapForBulkTasks(), action, username)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.#taskService.analytics?.taskActioned(
            action,
            tasks.map((task) => task?.processInstance?.processState),
            tasks.length
          )
          this.hideBulk.emit('success')
        },
        error: (error) => this.hideBulk.emit(error.message),
      })
  }

  bulkAssign(username: string): void {
    this.reset()
    this.bulkAction(TaskActions.ASSIGN, username)
  }

  reset(): void {
    this.closeOverlay()
    this.bulkAssignees.set([])
    this.loading.set(false)
    this.assigneesCalled.set(false)
    this.username.setValue('')
  }

  ngOnDestroy(): void {
    this.closeOverlay()
  }
}
